import { useState } from 'react';
import { BookOpen, ArrowLeft, ArrowRight, Plus, X } from 'lucide-react';

const COMMON_SUBJECTS = [
  'Mathematics', 'Science', 'English', 'Hindi', 'Social Science',
  'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Physical Education',
  'Art', 'Music', 'Economics', 'Accountancy', 'Business Studies',
  'History', 'Geography', 'Political Science', 'Psychology', 'Sociology'
];

export default function Step2_Subjects({ onNext, onBack, onSubmit, loading }) {
  const [subjectsByGrade, setSubjectsByGrade] = useState({
    '6': ['Mathematics', 'Science', 'English', 'Hindi', 'Social Science'],
    '7': ['Mathematics', 'Science', 'English', 'Hindi', 'Social Science'],
    '8': ['Mathematics', 'Science', 'English', 'Hindi', 'Social Science'],
    '9': ['Mathematics', 'Science', 'English', 'Hindi', 'Social Science'],
    '10': ['Mathematics', 'Science', 'English', 'Hindi', 'Social Science'],
    '11': ['Physics', 'Chemistry', 'Mathematics', 'English', 'Computer Science'],
    '12': ['Physics', 'Chemistry', 'Mathematics', 'English', 'Computer Science'],
  });

  const [newSubject, setNewSubject] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('6');

  const handleSubjectToggle = (grade, subject) => {
    setSubjectsByGrade(prev => ({
      ...prev,
      [grade]: prev[grade].includes(subject)
        ? prev[grade].filter(s => s !== subject)
        : [...prev[grade], subject]
    }));
  };

  const handleAddCustomSubject = () => {
    if (newSubject.trim() && !subjectsByGrade[selectedGrade].includes(newSubject.trim())) {
      setSubjectsByGrade(prev => ({
        ...prev,
        [selectedGrade]: [...prev[selectedGrade], newSubject.trim()]
      }));
      setNewSubject('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      subjectsByGrade: Object.entries(subjectsByGrade)
        .filter(([_, subjects]) => subjects.length > 0)
        .map(([grade, subjects]) => ({ grade, subjects }))
    };
    try {
      await onSubmit(payload);
      onNext();
    } catch (err) {
      // Error handled in parent
    }
  };

  return (
    <div>
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
          <BookOpen className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subjects Configuration</h2>
          <p className="text-gray-600">Configure subjects for each grade</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Grade Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Grade to Configure
          </label>
          <div className="flex gap-2 flex-wrap">
            {Object.keys(subjectsByGrade).map(grade => (
              <button
                key={grade}
                type="button"
                onClick={() => setSelectedGrade(grade)}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  selectedGrade === grade
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-300 hover:border-blue-400'
                }`}
              >
                Grade {grade}
              </button>
            ))}
          </div>
        </div>

        {/* Common Subjects */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Common Subjects for Grade {selectedGrade}
          </label>
          <div className="flex flex-wrap gap-2">
            {COMMON_SUBJECTS.map(subject => (
              <button
                key={subject}
                type="button"
                onClick={() => handleSubjectToggle(selectedGrade, subject)}
                className={`px-3 py-1.5 rounded-lg border-2 transition-all text-sm ${
                  subjectsByGrade[selectedGrade].includes(subject)
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-300 hover:border-blue-400'
                }`}
              >
                {subject}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Subject Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add Custom Subject
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomSubject())}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter custom subject name"
            />
            <button
              type="button"
              onClick={handleAddCustomSubject}
              className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add
            </button>
          </div>
        </div>

        {/* Selected Subjects for Current Grade */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Selected Subjects for Grade {selectedGrade}
          </label>
          <div className="flex flex-wrap gap-2">
            {subjectsByGrade[selectedGrade].map(subject => (
              <span
                key={subject}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm"
              >
                {subject}
                <button
                  type="button"
                  onClick={() => handleSubjectToggle(selectedGrade, subject)}
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? 'Saving...' : 'Continue'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
}
